/**
 * AdminSchedulesPage - 수업 관리(폼 3) 페이지
 *
 * /centers/:centerId/schedules 경로로 접근하며,
 * 센터의 수업(예약) 내역을 기간별로 조회하고 "수업 예약" 버튼으로 신규 예약을 추가한다.
 *
 * 수강권 관리(AdminTicketCatalogPage) / 회원 관리(AdminMembersListPage) 패턴을 따른다.
 * 목록 조회는 `useCenterSchedules`(→ getStudiomateSchedules CF)를 사용한다.
 */

import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useCurrentUser } from "@/components/AuthProvider";
import { useCenterContext } from "@/contexts/CenterContext";
import { CreateScheduleModal } from "@/components/CreateScheduleModal";
import { EditScheduleModal } from "@/components/EditScheduleModal";
import { ScheduleDetailModal } from "@/components/ScheduleDetailModal";
import {
  useCenterSchedules,
  useCancelBooking,
  useDeleteSchedule,
} from "@/hooks/useCenterSchedules";

/** Date → "YYYY-MM-DD" (로컬 = KST 가정). */
function toDateInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** 이번 달 1일~말일 범위를 기본 조회 기간으로 반환한다. */
function currentMonthRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { startDate: toDateInput(first), endDate: toDateInput(last) };
}

export function AdminSchedulesPage() {
  const { centerId } = useParams<{ centerId: string }>();
  const user = useCurrentUser();
  const { currentCenter } = useCenterContext();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [range, setRange] = useState(currentMonthRange);
  const [editScheduleId, setEditScheduleId] = useState<number | null>(null);
  const [detailScheduleId, setDetailScheduleId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const schedulesQuery = useCenterSchedules(Number(centerId), range);
  const schedules = schedulesQuery.data?.schedules ?? [];

  const cancelBooking = useCancelBooking();
  const deleteSchedule = useDeleteSchedule();

  const editSchedule = schedules.find((s) => s.id === editScheduleId) ?? null;
  const detailSchedule = schedules.find((s) => s.id === detailScheduleId) ?? null;

  const handleDeleteSchedule = async (scheduleId: number, title: string) => {
    if (!centerId) return;
    const confirmed = window.confirm(
      `수업 "${title}"을(를) 삭제하시겠습니까?\n과거 정산 기록은 보존되며, 목록과 강사 화면에서 제외됩니다.`,
    );
    if (!confirmed) return;
    setActionError(null);
    try {
      await deleteSchedule.mutateAsync({ centerId: Number(centerId), scheduleId });
      await schedulesQuery.refetch();
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : "수업 삭제에 실패했습니다.");
    }
  };

  const handleCancelBooking = async (scheduleId: number, bookingId: number) => {
    if (!centerId) return;
    const confirmed = window.confirm("이 예약을 취소하시겠습니까?");
    if (!confirmed) return;
    setActionError(null);
    try {
      await cancelBooking.mutateAsync({ centerId: Number(centerId), scheduleId, bookingId });
      await schedulesQuery.refetch();
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : "예약 취소에 실패했습니다.");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link to={`/centers/${centerId}`} className="text-blue-600 hover:text-blue-800 text-sm">
          &larr; 센터 상세
        </Link>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">수업 관리 ({schedules.length})</h1>
          <p className="text-sm text-gray-500 mt-1">센터의 수업 예약을 등록·확인합니다.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setCreateSuccess(false);
              setIsCreateOpen(true);
            }}
            disabled={!currentCenter}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            수업 예약
          </button>
        </div>
      </div>

      {createSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
          <p className="text-sm text-green-700">수업이 예약되었습니다.</p>
        </div>
      )}

      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <p className="text-sm text-red-700">{actionError}</p>
        </div>
      )}

      {/* 조회 기간 */}
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={range.startDate}
          onChange={(e) => setRange((prev) => ({ ...prev, startDate: e.target.value }))}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <span className="text-gray-400">~</span>
        <input
          type="date"
          value={range.endDate}
          onChange={(e) => setRange((prev) => ({ ...prev, endDate: e.target.value }))}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <button
          onClick={() => schedulesQuery.refetch()}
          className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
        >
          새로고침
        </button>
      </div>

      {/* 목록 */}
      {schedulesQuery.isLoading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : schedulesQuery.isError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-sm text-red-600">수업 목록을 불러오지 못했습니다.</p>
          <button
            onClick={() => schedulesQuery.refetch()}
            className="mt-3 px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50"
          >
            다시 시도
          </button>
        </div>
      ) : schedulesQuery.data?.error ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-700">{schedulesQuery.data.error.message}</p>
        </div>
      ) : schedules.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-700">
            해당 기간에 등록된 수업이 없습니다. "수업 예약" 버튼으로 추가하세요.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  날짜
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  시간
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  수업명
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  유형
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  강사
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  예약
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  관리
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {schedules.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{s.date}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {s.startTime}~{s.endTime}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {s.title}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {s.type === "G" ? "그룹" : "개인"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {s.staffName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {s.currentTrainees}/{s.maxTrainees}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <button
                      aria-label={`${s.title} 예약 관리`}
                      onClick={() => {
                        setActionError(null);
                        setDetailScheduleId(s.id);
                      }}
                      className="text-gray-600 hover:text-gray-800 mr-4"
                    >
                      예약
                    </button>
                    <button
                      aria-label={`${s.title} 수정`}
                      onClick={() => {
                        setActionError(null);
                        setEditScheduleId(s.id);
                      }}
                      className="text-blue-600 hover:text-blue-800 mr-4"
                    >
                      수정
                    </button>
                    <button
                      aria-label={`${s.title} 삭제`}
                      onClick={() => void handleDeleteSchedule(s.id, s.title)}
                      disabled={deleteSchedule.isPending}
                      className="text-red-600 hover:text-red-800 disabled:opacity-50"
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 수업 예약 모달 */}
      {currentCenter && (
        <CreateScheduleModal
          isOpen={isCreateOpen}
          ownerUid={user.uid}
          centerId={currentCenter.centerId}
          centerName={currentCenter.name}
          onClose={() => setIsCreateOpen(false)}
          onCreated={() => {
            setIsCreateOpen(false);
            setCreateSuccess(true);
            schedulesQuery.refetch();
          }}
        />
      )}

      {/* 수업 수정 모달 */}
      {editSchedule && centerId && (
        <EditScheduleModal
          isOpen={editSchedule !== null}
          centerId={Number(centerId)}
          schedule={editSchedule}
          onClose={() => setEditScheduleId(null)}
          onUpdated={() => {
            setEditScheduleId(null);
            schedulesQuery.refetch();
          }}
        />
      )}

      {/* 예약 관리(취소) 모달 */}
      {detailSchedule && centerId && (
        <ScheduleDetailModal
          isOpen={detailSchedule !== null}
          schedule={detailSchedule}
          onClose={() => setDetailScheduleId(null)}
          onCancelBooking={(bookingId) => void handleCancelBooking(detailSchedule.id, bookingId)}
          isCanceling={cancelBooking.isPending}
          errorMessage={actionError}
        />
      )}
    </div>
  );
}
